import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Snackbar } from '@atoms';
import { useCustomerId, useURLparams } from '@hooks';
import { FullWidthContent, MainMenuLayout } from '@layouts';
import { GoBackButton, Table } from '@molecules';
import { trackings } from '@utils';
import { NamelessCardBulkDetailResult, getResultColumns } from './constants';
import { styles } from './styles';
import { COLUMNS, DEFAULT_PAGE_LIMIT, NamelessCardBulk } from '../constants';
import useNamelessCardBulkDetail from '../data/useNamelessCardBulkDetail';
import useRetryNamelessCard from '../data/useRetryNamelessCard';
import { NAMELESS_CARDS_GO_BACK_CLICK_EVENT } from '../events';

/** Nameless card bulk detail screen */
const NamelessCardBulkDetailScreen = () => {
  const { dispersionId } = useParams() as { dispersionId: string };
  const { state } = useLocation() as { state: NamelessCardBulk };
  const { customerId } = useCustomerId();
  const navigate = useNavigate();
  const { params, setURLparams } = useURLparams<{ page: number; limit: number }>();
  const { data, isLoading, handleRowsPerPageChange, handlePageChange, handleRefresh } =
    useNamelessCardBulkDetail({
      dispersionId,
      params,
      setURLparams,
    });
  const { retry, retryingId, snackbarProps } = useRetryNamelessCard({
    onSuccess: () => handleRefresh(),
  });
  const { t } = useTranslation();

  const bulkData: NamelessCardBulk = {
    ...state,
    id: state?.id ?? dispersionId,
    customerId: state?.customerId ?? customerId,
    ...(data && {
      status: data.status,
      attemptedAmount: data.attemptedAmount,
      dispersedAmount: data.dispersedAmount,
      dispersedCount: data.dispersedCount,
    }),
  };

  /**
   * Retries a failed registration row
   */
  const handleRetry = (row: NamelessCardBulkDetailResult) => {
    retry({
      customerId: bulkData.customerId,
      namelessRegistrationId: bulkData.id,
      row,
    });
  };

  const resultColumns = getResultColumns({ onRetry: handleRetry, retryingId });

  const mergeBulkData = [{ ...bulkData, credited: data?.totalCount }];
  const detailHeaders = [
    ...COLUMNS,
    {
      title: t('DISPERSIONS:ACCREDITED'),
      content: (row: NamelessCardBulk & { credited?: number }) =>
        row?.credited && `${row.dispersedCount} / ${row?.credited} `,
    },
  ];

  return (
    <MainMenuLayout>
      <FullWidthContent>
        <Table columns={detailHeaders} items={mergeBulkData} disableControls />
        <Table
          isLoading={isLoading}
          columns={resultColumns}
          items={data?.page}
          count={data?.totalCount || 0}
          onPageChange={handlePageChange as (...args: Event[]) => null}
          onRowsPerPageChange={handleRowsPerPageChange as (...args: Event[]) => null}
          page={params.page || 1}
          rowsPerPage={params.limit || DEFAULT_PAGE_LIMIT}
          customStyle={styles.table}
        />
        <GoBackButton
          variant='outlined'
          text='COMMON:GO_BACK'
          onClick={() => {}}
          backFunction={() => {
            trackings(NAMELESS_CARDS_GO_BACK_CLICK_EVENT);
            navigate(-1);
          }}
        />
        <Snackbar {...snackbarProps} />
      </FullWidthContent>
    </MainMenuLayout>
  );
};

export default NamelessCardBulkDetailScreen;
